import React, { useState } from 'react';
import TeamSearchCombobox from './TeamSearchCombobox';
import TeamNotListedPanel from './TeamNotListedPanel';
import OnboardingErrorAlert from './OnboardingErrorAlert';
import { useAllTeams } from '../hooks/useAllTeams';
import { TeamOption, TeamSearchContactMetadata } from '../types/auth/team';
import Spinner from './Spinner';

const SIGNUP_TEAM_CONTACT: TeamSearchContactMetadata = {
  contact_email: 'info@athlytesports.com',
  team_not_listed_message:
    "Can't find your college? Submit a team request below or contact us at info@athlytesports.com.",
  request_team_path: '/api/request-team',
};

interface FormError {
  UserName?: string;
  Email?: string;
  fullName?: string;
  TeamId?: string;
  designation?: string;
  General?: string;
  onboardingContactEmail?: string;
}

interface CreateUserFormProps {
  handleCreateUser: (formData: any) => Promise<boolean | void>;
  username: string;
  setUsername: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  isPending?: boolean;
  error: FormError;
  setError: (error: FormError) => void;
  switchToLogin: () => void;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({
  handleCreateUser,
  username,
  setUsername,
  email,
  setEmail,
  isPending = false,
  error,
  setError,
  switchToLogin
}) => {
  const [fullName, setFullName] = useState('');
  const [designation, setDesignation] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);

  const {
    query,
    setQuery,
    results,
    loading: teamsLoading,
    error: teamsLoadError,
    retry: retryTeams,
    showEmptyResults,
  } = useAllTeams();

  const clearFieldError = (fieldName: keyof FormError) => {
    if (error[fieldName]) {
      setError({ ...error, [fieldName]: undefined });
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (value.trim()) {
      clearFieldError('UserName');
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value.trim() && /\S+@\S+\.\S+/.test(value)) {
      clearFieldError('Email');
    }
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    if (value.trim()) {
      clearFieldError('fullName');
    }
  };

  const handleDesignationChange = (value: string) => {
    setDesignation(value);
    if (value.trim()) {
      clearFieldError('designation');
    }
  };

  const handleTeamSelectionChange = (team: TeamOption | null) => {
    setSelectedTeam(team);
    if (team) {
      clearFieldError('TeamId');
      if (error.onboardingContactEmail) {
        setError({
          ...error,
          TeamId: undefined,
          General: undefined,
          onboardingContactEmail: undefined,
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormError = {};
    let isValid = true;

    if (!username.trim()) {
      newErrors.UserName = 'Username is required';
      isValid = false;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      newErrors.Email = 'Valid email is required';
      isValid = false;
    }

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }

    if (!selectedTeam) {
      newErrors.TeamId = 'Please select a college from the list.';
      isValid = false;
    }

    if (!isValid) {
      setError(newErrors);
      return;
    }

    const formData = {
      UserName: username.trim(),
      Email: email.trim(),
      fullName: fullName.trim(),
      TeamId: selectedTeam!.id,
      TeamName: selectedTeam!.name,
      designation: designation.trim()
    };

    await handleCreateUser(formData);
  };

  const inputBaseClasses = "w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-500";
  const errorInputClasses = "border-red-500 focus:ring-red-500";
  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";
  const isSubmitDisabled = isPending || teamsLoading || Boolean(teamsLoadError);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center w-full max-w-2xl bg-white p-8 sm:p-10 shadow-2xl rounded-xl">
      <div className="w-full flex flex-col">
        <div className="flex flex-col items-center w-full mb-8">
          <img loading="lazy" srcSet="/athlyte_logo.png" className="w-20 h-auto mb-3" alt="Athlyte Logo" />
          <h1 className="text-2xl font-bold text-gray-800">Create Your Athlyte Account</h1>
          <p className="text-gray-600 text-center mt-2">
            Join the community of athletes, coaches, and fans
          </p>
        </div>

        {error.General && error.onboardingContactEmail && (
          <OnboardingErrorAlert
            message={error.General}
            contactEmail={error.onboardingContactEmail}
            teamName={selectedTeam?.name || query.trim() || 'Unknown team'}
          />
        )}

        {error.General && !error.onboardingContactEmail && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            <p>{error.General}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="md:col-span-2">
            <TeamSearchCombobox
              query={query}
              setQuery={setQuery}
              results={results}
              loading={teamsLoading}
              loadError={teamsLoadError}
              onRetry={retryTeams}
              showEmptyResults={showEmptyResults}
              teamNotListedMessage={SIGNUP_TEAM_CONTACT.team_not_listed_message}
              selectedTeam={selectedTeam}
              onSelectedTeamChange={handleTeamSelectionChange}
              error={error.TeamId}
              disabled={isPending}
            />

            <TeamNotListedPanel
              contactMetadata={SIGNUP_TEAM_CONTACT}
              suggestedTeamName={query.trim()}
              userEmail={email}
              showEmptyState={showEmptyResults && !selectedTeam}
            />
          </div>

          <div>
            <label htmlFor="username" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Username
            </label>
            <input
              id="username"
              type="text"
              placeholder="Choose a username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              className={`${inputBaseClasses} ${error.UserName ? errorInputClasses : ""}`}
            />
            {error.UserName && <p className="mt-1 text-xs text-red-500">{error.UserName}</p>}
          </div>

          <div>
            <label htmlFor="email" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`${inputBaseClasses} ${error.Email ? errorInputClasses : ""}`}
            />
            {error.Email && <p className="mt-1 text-xs text-red-500">{error.Email}</p>}
          </div>

          <div>
            <label htmlFor="fullname" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Full Name
            </label>
            <input
              id="fullname"
              type="text"
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => handleFullNameChange(e.target.value)}
              className={`${inputBaseClasses} ${error.fullName ? errorInputClasses : ""}`}
            />
            {error.fullName && <p className="mt-1 text-xs text-red-500">{error.fullName}</p>}
          </div>

          <div>
            <label htmlFor="designation" className={`${labelClasses} after:content-["*"] after:ml-0.5 after:text-red-500`}>
              Designation
            </label>
            <input
              id="designation"
              type="text"
              placeholder="Your designation (e.g., coach, athlete)"
              value={designation}
              onChange={(e) => handleDesignationChange(e.target.value)}
              className={`${inputBaseClasses} ${error.designation ? errorInputClasses : ""}`}
            />
            {error.designation && <p className="mt-1 text-xs text-red-500">{error.designation}</p>}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center">
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="w-full py-2 px-4 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            {isPending ? (
              <div className="flex justify-center items-center">
                <Spinner size="sm" />
                <span className="ml-2">Creating account...</span>
              </div>
            ) : (
              'Create Account'
            )}
          </button>

          <div className="mt-4 text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={switchToLogin}
              className="text-cyan-600 hover:text-cyan-800 font-medium"
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateUserForm;
